// Statistik-Seite — A1 Durchlaufzeit Detail
// Tabelle Overview + Pro-Auftrag-Liste + SVG-Bar-Chart

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  type DurchlaufzeitOverview,
  type DurchlaufzeitAuftrag,
  type TopLauferAuftrag,
} from '../lib/api';
import { Clock, AlertTriangle, Info, ChevronLeft } from 'lucide-react';

const MODULE = ['drueckteile', 'laufraeder', 'baugruppen', 'montage'] as const;
type Modul = (typeof MODULE)[number];

export default function Statistik() {
  const [overview, setOverview] = useState<DurchlaufzeitOverview | null>(null);
  const [modul, setModul] = useState<Modul>('drueckteile');
  const [rows, setRows] = useState<DurchlaufzeitAuftrag[]>([]);
  const [topLaufer, setTopLaufer] = useState<TopLauferAuftrag[]>([]);
  const [days, setDays] = useState(90);
  const [cleanOnly, setCleanOnly] = useState(false);

  useEffect(() => {
    api.statistik.durchlaufzeitOverview(days).then(setOverview);
  }, [days]);

  useEffect(() => {
    api.statistik
      .durchlaufzeitByAuftrag(modul, 100, cleanOnly)
      .then((r) => setRows(r.data));
  }, [modul, cleanOnly]);

  useEffect(() => {
    api.statistik.topLaufer(15).then((r) => setTopLaufer(r.data));
  }, []);

  const modulData = overview?.modules.find((m) => m.modul === modul);
  const max = rows.length
    ? Math.max(...rows.map((r) => r.dl_tage_kalender ?? 0))
    : 1;
  const p90 = modulData?.all.p90_kalender ?? 0;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 text-sm">
            <ChevronLeft className="w-4 h-4" /> Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Clock className="w-6 h-6" /> Durchlaufzeit-Statistik
          </h1>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(+e.target.value)}
          className="border border-slate-300 rounded px-3 py-1.5 text-sm"
        >
          <option value={30}>30 Tage</option>
          <option value={90}>90 Tage</option>
          <option value={180}>180 Tage</option>
          <option value={365}>365 Tage</option>
        </select>
      </div>

      {/* Data-Quality-Banner */}
      {overview?.note && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg text-sm">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">Datenqualität: {overview.data_quality}</div>
            <div>{overview.note}</div>
            <div className="mt-1 text-xs">
              Gesamt: {overview.total_records} Einträge · Sauber: {overview.clean_records}
            </div>
          </div>
        </div>
      )}

      {/* Overview-Tabelle */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800">Übersicht je Modul</h2>
          <span className="text-xs text-slate-500">Letzte {days} Tage</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Modul</th>
                <th className="text-right px-4 py-2" title="Alle inkl. Cleanup">Alle</th>
                <th className="text-right px-4 py-2" title="Nur saubere Daten">Sauber</th>
                <th className="text-right px-4 py-2">Ø Tage</th>
                <th className="text-right px-4 py-2">P50</th>
                <th className="text-right px-4 py-2">P90</th>
                <th className="text-right px-4 py-2">Ø Aktiv</th>
              </tr>
            </thead>
            <tbody>
              {overview?.modules.map((m) => {
                const metric = m.clean.count > 0 ? m.clean : m.all;
                const noClean = m.clean.count === 0;
                return (
                  <tr
                    key={m.modul}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setModul(m.modul as Modul)}
                  >
                    <td className="px-4 py-2 capitalize font-medium">{m.modul}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">{m.all.count}</td>
                    <td className={`px-4 py-2 text-right tabular-nums ${noClean ? 'text-red-400' : 'text-green-700'}`}>
                      {m.clean.count}
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums font-semibold ${noClean ? 'text-slate-400' : ''}`}>
                      {metric.count ? metric.avg_kalender : '—'}
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums ${noClean ? 'text-slate-400' : ''}`}>
                      {metric.count ? metric.p50_kalender : '—'}
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums ${noClean ? 'text-slate-400' : ''}`}>
                      {metric.count ? metric.p90_kalender : '—'}
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums ${noClean ? 'text-slate-400' : ''}`}>
                      {metric.avg_aktiv ? metric.avg_aktiv : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top-Laufer (aktive, lange laufende Aufträge) */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <h2 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          Langläufer (aktiv)
        </h2>
        {topLaufer.length === 0 ? (
          <div className="text-sm text-slate-500">Keine aktiven Aufträge mit Bearbeitungsstart</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left py-1">Modul</th>
                <th className="text-left py-1">Bestellnr</th>
                <th className="text-left py-1">Liefertermin</th>
                <th className="text-left py-1">Aktiv seit</th>
                <th className="text-right py-1">Tage</th>
              </tr>
            </thead>
            <tbody>
              {topLaufer.map((t) => (
                <tr key={`${t.modul}-${t.id}`} className="border-t border-slate-100">
                  <td className="py-1.5 capitalize">{t.modul}</td>
                  <td className="py-1.5 font-medium">
                    <Link
                      to={t.modul === 'drueckteile' ? `/drueckteile/${t.id}` : `/${t.modul}`}
                      className="text-brand hover:underline"
                    >
                      {t.bestellnr}
                    </Link>
                  </td>
                  <td className="py-1.5 text-slate-500">{t.lieferdatum?.slice(0, 10)}</td>
                  <td className="py-1.5 text-slate-500">{t.first_active_at?.slice(0, 10)}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${t.tage_aktiv > 7 ? 'text-red-600' : ''}`}>
                    {t.tage_aktiv.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pro-Auftrag Detail */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="font-semibold text-slate-800">Pro-Auftrag Durchlaufzeit</h2>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={cleanOnly}
                onChange={(e) => setCleanOnly(e.target.checked)}
                className="accent-brand"
              />
              Nur saubere Daten
            </label>
            <select
              value={modul}
              onChange={(e) => setModul(e.target.value as Modul)}
              className="border border-slate-300 rounded px-3 py-1.5 text-sm capitalize"
            >
              {MODULE.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* SVG-Bar-Chart */}
        {rows.length > 0 && (
          <svg
            viewBox={`0 0 ${Math.max(rows.slice(0, 60).length * 14, 300)} 120`}
            className="w-full h-32 mb-4"
          >
            {rows.slice(0, 60).map((r, i) => {
              const v = r.dl_tage_kalender ?? 0;
              const h = Math.max(1, (v / (max || 1)) * 100);
              const color = r.is_cleanup_artifact === 1
                ? '#94a3b8'
                : v > p90 ? '#dc2626' : '#6366f1';
              return (
                <rect key={r.id} x={i * 14} y={110 - h} width={12} height={h} fill={color}>
                  <title>
                    {r.bestellnr} · {v.toFixed(1)} Tage
                    {r.is_cleanup_artifact === 1 ? ' (cleanup)' : ''}
                  </title>
                </rect>
              );
            })}
            <line x1="0" y1="110" x2="100%" y2="110" stroke="#cbd5e1" />
          </svg>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b border-slate-100">
              <tr>
                <th className="text-left py-1">Bestellnr</th>
                <th className="text-left py-1">Kunde</th>
                <th className="text-left py-1">Angelegt</th>
                <th className="text-left py-1">Fertig</th>
                <th className="text-right py-1">Tage (kal.)</th>
                <th className="text-right py-1">Tage (aktiv)</th>
                <th className="text-center py-1">Typ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 last:border-0">
                  <td className="py-1.5 font-medium">{r.bestellnr}</td>
                  <td className="py-1.5 text-slate-600">{r.kunde_name ?? '—'}</td>
                  <td className="py-1.5 text-slate-500">{r.created_at?.slice(0, 10)}</td>
                  <td className="py-1.5 text-slate-500">{r.fertig_datum?.slice(0, 10)}</td>
                  <td
                    className={`py-1.5 text-right tabular-nums font-semibold ${
                      (r.dl_tage_kalender ?? 0) > p90 && !r.is_cleanup_artifact ? 'text-red-600' : ''
                    }`}
                  >
                    {(r.dl_tage_kalender ?? 0).toFixed(1)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-slate-600">
                    {r.dl_tage_aktiv != null ? r.dl_tage_aktiv.toFixed(1) : '—'}
                  </td>
                  <td className="py-1.5 text-center">
                    {r.is_cleanup_artifact === 1 ? (
                      <span className="inline-block bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[10px]">
                        Cleanup
                      </span>
                    ) : (
                      <span className="inline-block bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px]">
                        Sauber
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-slate-500">
                    Keine Daten für {modul}
                    {cleanOnly ? ' (nur saubere)' : ''}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
